import { isInstalledApp } from '@/lib/appUrl';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Bell, X } from 'lucide-react';

const DISMISS_KEY = 'pwa_install_banner_dismissed_at';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export const PwaInstallBanner = () => {
  const [visible, setVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    if (isInstalledApp()) return;

    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (raw) {
        const ts = parseInt(raw, 10);
        if (!Number.isNaN(ts) && Date.now() - ts < DISMISS_TTL_MS) return;
      }
    } catch (_) {}

    setVisible(true);

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const installed = () => setVisible(false);
    window.addEventListener('appinstalled', installed);
    window.addEventListener('beforeinstallprompt', handler);
    return () => {
      window.removeEventListener('appinstalled', installed);
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      if (outcome === 'accepted') setVisible(false);
    }
  };

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (_) {}
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="px-4 pt-3 md:px-6">
      <Card className="p-4 flex items-start gap-3 border-primary/30 bg-primary/5">
        <div className="h-10 w-10 rounded-lg bg-primary/15 flex items-center justify-center shrink-0">
          <Bell className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm md:text-base">
            Non perdere i promemoria delle partite
          </p>
          <p className="text-xs md:text-sm text-muted-foreground mt-1">
            Installa LeagueHub sul telefono per ricevere notifiche su iscrizioni,
            promozioni da riserva e reminder partita anche con il browser chiuso.
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            {deferredPrompt ? (
              <Button size="sm" onClick={handleInstall} data-testid="pwa-install-btn">
                Installa app
              </Button>
            ) : (
              <Button size="sm" asChild>
                <Link to="/install-pwa" data-testid="pwa-install-guide-link">
                  Come installare
                </Link>
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={handleDismiss}>
              Non ora
            </Button>
          </div>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="text-muted-foreground hover:text-foreground"
          aria-label="Chiudi"
        >
          <X className="h-4 w-4" />
        </button>
      </Card>
    </div>
  );
};

export default PwaInstallBanner;
